import { z } from '@/utils/zod'
import { ClientAppTypeSchema, type ClientAppType } from '@/entities/common/ClientAppType'
import { EntitySchema } from '@/entities/Entity'

export const NotificationCategories = [
    'community.reply',
    'feedback.comment',
    'export',
    'job',
    'review',
    'questionnaire.submission',
    'contact.resubmission',
    'contact.rule',
    'conversation.inbox',
    'booking',
    'network',
    'teams',
    'selfService',
    'todo',
    'compensation',
    'workHours',
    'donation',
    'social',
] as const

export const NotificationCategorySchema = z.enum(NotificationCategories)

export type NotificationCategory = z.infer<typeof NotificationCategorySchema>

export const notificationCategoriesByTarget = {
    admin: [
        'export',
        'job',
        'review',
        'questionnaire.submission',
        'contact.resubmission',
        'contact.rule',
        'conversation.inbox',
        'todo',
        'donation',
        'social',
    ],
    connect: ['booking', 'network', 'selfService', 'teams', 'compensation', 'workHours', 'todo'],
    embeddable: [],
    root: ['community.reply', 'feedback.comment'],
} satisfies Record<ClientAppType, NotificationCategory[]>

export const NotificationLinkSchema = z.object({
    caption: z.string().min(1).max(32),
    to: z.string().min(1).max(2048),
    external: z.boolean(),
})

export type NotificationLink = z.infer<typeof NotificationLinkSchema>

export const notificationTitleMaxLength = 100

export const NotificationSchema = EntitySchema.extend({
    organization: z.objectId.nullable(),
    chat: z.object({ conversation: z.objectId, rootActivity: z.objectId, activity: z.objectId }).optional(),
    userId: z.string(),
    contact: z.objectId.nullable().optional(),
    contactName: z.string().optional(),
    key: z.string().optional(),
    aggregate: z.boolean(),
    lastActivityAt: z.isoDateTimeWithMilliseconds,
    target: ClientAppTypeSchema,
    category: NotificationCategorySchema,
    emoji: z.string().min(1).max(16),
    title: z.string().max(notificationTitleMaxLength),
    message: z.string().max(480),
    details: z.string().max(5000),
    link: NotificationLinkSchema.nullable(),
    expiresAt: z.isoDateTime.nullable(),
    sendEmail: z.boolean(),
    sendPush: z.boolean(),
    readAt: z.isoDateTime.nullable(),
    cleanupRequestedAt: z.isoDateTime.nullable(),
})

export type Notification = z.infer<typeof NotificationSchema>
